/**
 * The signed-in person's own memory notes for a customer (agent runner plan
 * M4, item 5): what Claude has written about them in their chats there.
 *
 * Read-only. The request names the customer and nobody else: the server takes
 * the person from the session, so there is no way to ask for a colleague's.
 *
 * customer is the customer NAME (account.customer.name).
 */
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'

/** One note file. Mirrors agentrunner.MemoryFile. */
export interface AgentMemoryFile {
  /** Path below the memory folder, "/" between folders. */
  name: string
  size: number
  /** ISO time. */
  modified_at: string
  /** The file's text; empty when `skipped` says why. */
  content: string
  skipped?: 'not_text' | 'too_large'
}

/** Mirrors agentrunner.MemoryListing. */
export interface AgentMemoryListing {
  files: AgentMemoryFile[]
  /** The folder holds more than the listing carries. */
  truncated: boolean
}

export interface UseAgentMemoryReturn {
  files: AgentMemoryFile[]
  truncated: boolean
  loading: boolean
  /** Set when the notes could not be read; never when there are simply none. */
  error: unknown
}

export const useAgentMemory = (customer: string | undefined): UseAgentMemoryReturn => {
  const name = (customer || '').trim()
  const query = useQuery<AgentMemoryListing, unknown>({
    queryKey: ['agent-memory', name.toLowerCase()],
    queryFn: async () =>
      (await axios.get<AgentMemoryListing>(`${API_BASE_URL}/agent-memory/${encodeURIComponent(name)}`)).data,
    enabled: name !== '',
    retry: false,
  })
  return {
    files: query.data?.files ?? [],
    truncated: !!query.data?.truncated,
    loading: query.isPending && name !== '',
    error: query.isError ? query.error : undefined,
  }
}

export default useAgentMemory
